import { useCallback, useEffect, useState } from "react";
import { Check, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { Pagination } from "@/components/Pagination";
import { adminPaymentService } from "@/services/paymentService";
import { errorMessage } from "@/services/apiClient";
import { fmtDate, fmtMoney } from "@/lib/format";
import { ADMIN_NAV } from "./nav";

const input = "h-11 w-full rounded-md border border-[#22252F] bg-[#0F1116] px-3 text-sm text-white outline-none focus:border-[#2563EB]";
const TYPES = [{ v: "btc", l: "BTC" }, { v: "usdt", l: "USDT" }, { v: "pix", l: "PIX" }, { v: "card", l: "Cartão" }, { v: "iban", l: "IBAN" }];
const STATUS = { pending: "text-[#F59E0B]", approved: "text-[#10B981]", rejected: "text-[#EF4444]" };

export default function AdminPaymentsPage() {
  const [methods, setMethods] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [form, setForm] = useState({ type: "btc", label: "", address: "", network: "", instructions: "" });
  const [depPage, setDepPage] = useState({ page: 1, per_page: 20, pages: 1, total: 0 });
  const [wdPage, setWdPage] = useState({ page: 1, per_page: 20, pages: 1, total: 0 });

  const load = useCallback(async () => {
    try {
      const [m, d, w] = await Promise.all([
        adminPaymentService.methods(),
        adminPaymentService.deposits({ page: depPage.page, per_page: depPage.per_page }),
        adminPaymentService.withdrawals({ page: wdPage.page, per_page: wdPage.per_page }),
      ]);
      setMethods(m);
      setDeposits(d.items);
      setWithdrawals(w.items);
      setDepPage((prev) => ({ ...prev, pages: d.pages, total: d.total, page: d.page }));
      setWdPage((prev) => ({ ...prev, pages: w.pages, total: w.total, page: w.page }));
    } catch (e) { toast.error(errorMessage(e)); }
  }, [depPage.page, depPage.per_page, wdPage.page, wdPage.per_page]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await adminPaymentService.createMethod(form);
      toast.success("Método adicionado");
      setForm({ type: "btc", label: "", address: "", network: "", instructions: "" });
      load();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const review = async (kind, id, action) => {
    try {
      kind === "deposit"
        ? await adminPaymentService.reviewDeposit(id, action)
        : await adminPaymentService.reviewWithdrawal(id, action);
      toast.success(action === "approve" ? "Aprovado" : "Recusado");
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const clearHistory = async (kind) => {
    try {
      const res = kind === "deposit"
        ? await adminPaymentService.clearDeposits()
        : await adminPaymentService.clearWithdrawals();
      toast.success(`${res.deleted} registo(s) apagado(s) do histórico`);
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const Requests = ({ title, rows, kind, testId, pagination, setPagination }) => (
    <section className="rounded-md border border-[#22252F] bg-[#12141A] p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-lg font-light tracking-tight text-white">{title}</h2>
        <button data-testid={`clear-${kind}-history`} onClick={() => clearHistory(kind)}
                className="flex items-center gap-2 rounded-md border border-[#EF4444]/40 px-3 py-2 text-xs text-[#FCA5A5] transition-colors hover:bg-[#EF4444]/10">
          <Trash2 className="h-3.5 w-3.5" /> Apagar histórico
        </button>
      </div>
      <div className="mt-4 space-y-2" data-testid={testId}>
        {rows.map((r) => (
          <div key={r.id} data-testid={`${kind}-${r.id}`}
               className="flex flex-wrap items-center justify-between gap-3 border-b border-[#1A1D25] py-3 text-sm">
            <div className="min-w-0">
              <p className="font-mono text-white">{fmtMoney(r.amount)} <span className="text-xs text-[#94A3B8]">{r.user_email}</span></p>
              <p className="truncate text-xs text-[#94A3B8]">
                {r.method_label || r.account_label} {r.account_details ? `· ${r.account_details}` : ""} · {fmtDate(r.created_at)}
                {r.reference ? ` · ref ${r.reference}` : ""}
              </p>
            </div>
            {r.status === "pending" ? (
              <div className="flex gap-2">
                <button onClick={() => review(kind, r.id, "approve")} data-testid={`approve-${kind}-${r.id}`}
                        className="rounded-md border border-[#10B981]/50 p-2 text-[#10B981] hover:bg-[#10B981]/10">
                  <Check className="h-4 w-4" />
                </button>
                <button onClick={() => review(kind, r.id, "reject")} data-testid={`reject-${kind}-${r.id}`}
                        className="rounded-md border border-[#EF4444]/50 p-2 text-[#EF4444] hover:bg-[#EF4444]/10">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <span className={`font-mono text-xs uppercase ${STATUS[r.status]}`}>{r.status}</span>
            )}
          </div>
        ))}
        {rows.length === 0 ? <p className="py-8 text-center text-sm text-[#64748B]">Nada pendente</p> : null}
      </div>
      <Pagination
        testId={`${kind}-pagination`}
        page={pagination.page}
        pages={pagination.pages}
        total={pagination.total}
        perPage={pagination.per_page}
        onPage={(p) => setPagination((prev) => ({ ...prev, page: p }))}
        onPerPage={(n) => setPagination((prev) => ({ ...prev, per_page: n, page: 1 }))}
      />
    </section>
  );

  return (
    <AppShell nav={ADMIN_NAV} title="Pagamentos" subtitle="Métodos, depósitos e retiradas">
      <div data-testid="admin-payments-page" className="space-y-6">
        <section className="rounded-md border border-[#22252F] bg-[#12141A] p-6">
          <h2 className="font-heading text-lg font-light tracking-tight text-white">Métodos de pagamento</h2>
          <div className="mt-4 space-y-2">
            {methods.map((m) => (
              <div key={m.id} data-testid={`method-row-${m.id}`}
                   className="flex flex-wrap items-center justify-between gap-3 border-b border-[#1A1D25] py-3 text-sm">
                <div className="min-w-0">
                  <p className="text-white">{m.label} <span className="font-mono text-xs text-[#60A5FA]">{m.type.toUpperCase()}</span></p>
                  <p className="truncate font-mono text-xs text-[#94A3B8]">{m.address}{m.network ? ` · ${m.network}` : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => adminPaymentService.updateMethod(m.id, { active: !m.active }).then(load)}
                          data-testid={`toggle-method-${m.id}`}
                          className="rounded-md border border-[#22252F] px-3 py-2 text-xs text-[#94A3B8] hover:text-white">
                    {m.active ? "Desativar" : "Ativar"}
                  </button>
                  <button onClick={() => adminPaymentService.deleteMethod(m.id).then(load)} data-testid={`delete-method-${m.id}`}
                          className="rounded-md border border-[#22252F] p-2 text-[#94A3B8] hover:text-[#EF4444]">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={create} className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="create-method-form">
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                    data-testid="method-type-select" className={input}>
              {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
            <input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                   placeholder="Nome visível ao cliente" data-testid="method-label-input" className={input} />
            <input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                   placeholder="Carteira / chave PIX" data-testid="method-address-input" className={`${input} sm:col-span-2`} />
            <input value={form.network} onChange={(e) => setForm({ ...form, network: e.target.value })}
                   placeholder="Rede (ex: TRC20)" data-testid="method-network-input" className={input} />
            <input value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })}
                   placeholder="Instruções (opcional)" data-testid="method-instructions-input" className={input} />
            <button type="submit" data-testid="create-method-submit"
                    className="flex h-11 items-center justify-center gap-2 rounded-md bg-[#2563EB] text-xs font-semibold uppercase tracking-[0.15em] text-white hover:bg-[#1D4ED8] sm:col-span-2">
              <Plus className="h-4 w-4" /> Adicionar método
            </button>
          </form>
        </section>

        <Requests title="Depósitos" rows={deposits} kind="deposit" testId="admin-deposits-list"
                  pagination={depPage} setPagination={setDepPage} />
        <Requests title="Retiradas" rows={withdrawals} kind="withdrawal" testId="admin-withdrawals-list"
                  pagination={wdPage} setPagination={setWdPage} />
      </div>
    </AppShell>
  );
}
